// @file Global state for SurfaceGroupPicker.
//
// Manages the group picker modal's visibility and provides a promise-based API
// for awaiting user selection.
//
// Usage:
//   const result = await useSurfaceGroupPickerStore().show()
//   if (result) {
//     const { groupId } = result // section id, color id, or property value
//   }
//   // result is null if user cancelled
import type { WallGroupPost } from '@@/types'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useSurfaceGroupPickerStore = defineStore('surfaceGroupPicker', () => {
  const isVisible = ref(false)
  let resolvePromise: ((result: { groupId: WallGroupPost['id'] } | null) => void) | null = null

  function show(): Promise<{ groupId: WallGroupPost['id'] } | null> {
    // there should only be one instance of the group picker visible at a time,
    // so if it's already opened with a pending promise, throw an error
    if (resolvePromise !== null) {
      throw new Error('Group picker is already visible')
    }
    isVisible.value = true
    return new Promise((resolve) => {
      resolvePromise = resolve
    })
  }

  function pickGroup(groupId: WallGroupPost['id']) {
    isVisible.value = false
    resolvePromise?.({ groupId })
    resolvePromise = null
  }

  function cancel() {
    isVisible.value = false
    resolvePromise?.(null)
    resolvePromise = null
  }

  return { isVisible, show, pickGroup, cancel }
})
